import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Navbar from "@/components/Navbar";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { EventCard } from "@/components/Cards";
import { Calendar, Globe, Heart, Instagram, Link2, MapPin, MessageCircle, Share2, Sparkles, Youtube } from "lucide-react";
import { toast } from "sonner";

export default function CreatorProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [creator, setCreator] = useState(null);
  const [loading, setLoading] = useState(true);
  const [events, setEvents] = useState([]);
  const [following, setFollowing] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get(`/creators/${id}`).then((r) => {
      if (alive) setCreator(r.data);
    }).catch(() => {
      if (alive) setCreator(null);
    }).finally(() => {
      if (alive) setLoading(false);
    });
    return () => { alive = false; };
  }, [id]);

  useEffect(() => {
    if (!creator) return;
    const params = new URLSearchParams();
    const language = first(creator.creator_languages || creator.language_preferences, "");
    const city = first(creator.creator_locations || creator.city_preferences, "");
    if (language && language !== "All") params.set("language", language);
    if (city && city !== "All UK") params.set("city", city);
    api.get(`/events?${params.toString()}`)
      .then((r) => setEvents((r.data || []).slice(0, 8)))
      .catch(() => setEvents([]));
  }, [creator]);

  useEffect(() => {
    if (!user || !creator) return;
    api.get(`/creators/${creator.user_id}/follow-status`)
      .then((r) => setFollowing(Boolean(r.data?.following)))
      .catch(() => setFollowing(false));
  }, [user, creator]);

  if (loading) return <div className="min-h-screen bg-background"><Navbar /></div>;

  if (!creator) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <main className="page-shell py-12">
          <div className="rounded-2xl border border-border p-8">
            <p className="font-display text-2xl font-black">Creator not found</p>
            <p className="mt-2 text-sm text-muted-foreground">This profile may be hidden or disabled.</p>
            <Button className="mt-4 rounded-full" onClick={() => navigate("/creators")}>Back to creators</Button>
          </div>
        </main>
      </div>
    );
  }

  const name = creator.display_name || creator.name || "Namaste creator";
  const categories = creator.creator_categories || [];
  const interests = creator.creator_interests || [];
  const languages = creator.creator_languages || creator.language_preferences || [];
  const locations = creator.creator_locations || creator.city_preferences || [];
  const profileUrl = typeof window !== "undefined" ? `${window.location.origin}/creators/${creator.user_id}` : "";
  const shareText = `${name} on NamasteNetwork.ai`;
  const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(`${shareText}\n${profileUrl}`)}`;

  const shareProfile = async () => {
    if (navigator.share) {
      await navigator.share({ title: name, text: shareText, url: profileUrl }).catch(() => {});
      return;
    }
    await navigator.clipboard.writeText(profileUrl);
    toast.success("Creator link copied");
  };

  const toggleFollow = async () => {
    if (!user) {
      navigate("/login");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/creators/${creator.user_id}/follow`, { following: !following });
      setFollowing(Boolean(data.following));
      setCreator((current) => ({ ...current, follower_count: data.follower_count }));
      toast.success(data.following ? "Creator followed" : "Creator unfollowed");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update follow");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <section className="relative overflow-hidden border-b border-border">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,hsl(var(--primary)/0.16),transparent_55%)]" />
        <div className="relative page-shell py-10 md:py-14">
          <div className="grid gap-8 lg:grid-cols-[1fr_340px] lg:items-end">
            <div className="flex flex-col gap-5 md:flex-row md:items-end">
              <Avatar className="h-28 w-28 border border-border">
                <AvatarImage src={creator.profile_image_url || creator.picture} />
                <AvatarFallback className="text-3xl">{name.slice(0, 1)}</AvatarFallback>
              </Avatar>
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  {creator.featured_creator && <Badge className="bg-primary text-primary-foreground"><Sparkles className="mr-1 h-3.5 w-3.5" />Featured</Badge>}
                  {categories.slice(0, 3).map((item) => <Badge key={item} variant="secondary">{item}</Badge>)}
                </div>
                <h1 className="mt-3 font-display text-4xl md:text-6xl font-black leading-tight">{name}</h1>
                {creator.instagram_handle && <p className="mt-2 text-lg font-bold text-primary">{creator.instagram_handle}</p>}
                <div className="mt-3 flex flex-wrap gap-4 text-sm text-muted-foreground">
                  <span className="inline-flex items-center gap-1.5"><MapPin className="h-4 w-4 text-primary" />{locations.slice(0, 3).join(", ") || "UK"}</span>
                  <span className="inline-flex items-center gap-1.5"><Globe className="h-4 w-4 text-primary" />{languages.slice(0, 3).join(", ") || "All languages"}</span>
                  <span className="inline-flex items-center gap-1.5"><Heart className="h-4 w-4 text-primary" />{creator.follower_count || 0} interested</span>
                </div>
              </div>
            </div>
            <div className="rounded-2xl border border-border bg-card p-4">
              <Button onClick={toggleFollow} disabled={busy || user?.user_id === creator.user_id} className="w-full rounded-full">
                <Heart className={`mr-2 h-4 w-4 ${following ? "fill-current" : ""}`} />
                {following ? "Following" : "I'm interested"}
              </Button>
              <div className="mt-3 grid grid-cols-2 gap-2">
                <Button onClick={shareProfile} variant="outline" className="rounded-full"><Share2 className="mr-2 h-4 w-4" />Share</Button>
                <Button asChild variant="outline" className="rounded-full"><a href={whatsappUrl} target="_blank" rel="noreferrer"><MessageCircle className="mr-2 h-4 w-4" />WhatsApp</a></Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <main className="page-shell py-10 space-y-10">
        <section className="grid gap-6 lg:grid-cols-[1fr_360px]">
          <div className="rounded-2xl border border-border p-6">
            <p className="text-xs uppercase tracking-[0.25em] font-black text-primary">About</p>
            <p className="mt-4 text-base leading-7 text-foreground/90">{creator.bio || "This creator has not added a bio yet."}</p>
            {interests.length > 0 && (
              <div className="mt-6 flex flex-wrap gap-2">
                {interests.map((item) => <Badge key={item} variant="outline">{item}</Badge>)}
              </div>
            )}
          </div>
          <div className="rounded-2xl border border-border p-6">
            <p className="text-xs uppercase tracking-[0.25em] font-black text-primary">Connect</p>
            <div className="mt-4 flex flex-wrap gap-2">
              {creator.instagram_url && <Button asChild variant="outline" className="rounded-full"><a href={creator.instagram_url} target="_blank" rel="noreferrer"><Instagram className="mr-2 h-4 w-4" />Instagram</a></Button>}
              {creator.youtube_url && <Button asChild variant="outline" className="rounded-full"><a href={creator.youtube_url} target="_blank" rel="noreferrer"><Youtube className="mr-2 h-4 w-4" />YouTube</a></Button>}
              {creator.website_url && <Button asChild variant="outline" className="rounded-full"><a href={creator.website_url} target="_blank" rel="noreferrer"><Link2 className="mr-2 h-4 w-4" />Website</a></Button>}
            </div>
          </div>
        </section>

        {events.length > 0 && (
          <section>
            <div className="mb-4 flex items-end justify-between gap-3">
              <div>
                <p className="text-xs uppercase tracking-[0.25em] font-black text-primary">Their vibe</p>
                <h2 className="font-display text-2xl font-black">Events you may like</h2>
              </div>
              <Button variant="ghost" className="rounded-full" onClick={() => navigate("/")}>Browse all</Button>
            </div>
            <div className="flex gap-5 overflow-x-auto no-scrollbar pb-2">
              {events.map((event) => <EventCard key={event.event_id} event={event} />)}
            </div>
          </section>
        )}
      </main>
    </div>
  );
}

function first(values, fallback) {
  return Array.isArray(values) && values.length ? values[0] : fallback;
}
